import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import {
  getReport, createReport, updateReport, getFactors,
  listOrganizations, calculateInline, exportReport, fmt,
} from "../lib/api";
import { ArrowLeft, Save, Download, Loader2, AlertCircle, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import ReportInfo from "../components/report/ReportInfo";
import Kategori1 from "../components/report/Kategori1";
import Kategori2 from "../components/report/Kategori2";
import Kategori3 from "../components/report/Kategori3";
import Kategori4 from "../components/report/Kategori4";
import Kategori5 from "../components/report/Kategori5";
import Kategori6 from "../components/report/Kategori6";
import Summary from "../components/report/Summary";

const TABS = [
  { id: "info", label: "Tesis Bilgileri" },
  { id: "k1", label: "Kategori 1" },
  { id: "k2", label: "Kategori 2" },
  { id: "k3", label: "Kategori 3" },
  { id: "k4", label: "Kategori 4" },
  { id: "k5", label: "Kategori 5" },
  { id: "k6", label: "Kategori 6" },
  { id: "summary", label: "Özet" },
];

const emptyData = {
  k1: { sabit_yanma: {}, hareketli_yanma: {}, kacak: {} },
  k2: { kaynak_id: "el_tr", elektrik_yillik: 0, elektrik_aylik: {} },
  k3: { calisan: {}, tasinim: {}, is_seyahati: {}, konaklama_ulke: "konak_turkiye", konaklama_fv: 0 },
  k4: { su: 0, atiksu: 0, atik: {}, satin_alinan_items: [], yakit_uretimi: {} },
  k5: { urun_ton: 0, ef_override: {} },
  k6: { kaynak_id: "k6_td_tr", yillik: 0, aylik: {} },
};

export default function ReportEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();

  const [tab, setTab] = useState("info");
  const [factors, setFactors] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const currentYear = new Date().getFullYear() - 1;

  const [form, setForm] = useState({
    organization_id: sp.get("org") || "",
    year: currentYear,
    report_date: new Date().toISOString().slice(0, 10),
    period_start: `${currentYear}-01-01`,
    period_end: `${currentYear}-12-31`,
    frequency: "12 aylık",
    base_year: currentYear,
    prepared_by_name: "",
    prepared_by_title: "",
    prepared_by_email: "",
    notes: "",
    data: emptyData,
  });

  useEffect(() => {
    Promise.all([getFactors(), listOrganizations()])
      .then(([f, o]) => { setFactors(f); setOrgs(o); })
      .catch(() => toast.error("Veriler yüklenemedi"));
  }, []);

  useEffect(() => {
    if (id) {
      getReport(id).then((r) => {
        setForm({ ...r, data: mergeWithDefaults(r.data || {}) });
        setResults(r.results || null);
      }).catch(() => toast.error("Rapor yüklenemedi")).finally(() => setLoading(false));
    } else { setLoading(false); }
  }, [id]);

  // Canlı hesaplama (debounce)
  useEffect(() => {
    if (!factors) return;
    setCalculating(true);
    const t = setTimeout(() => {
      calculateInline(form.data).then(setResults).catch(() => {}).finally(() => setCalculating(false));
    }, 350);
    return () => clearTimeout(t);
  }, [form.data, factors]);

  const setData = (updater) => setForm((f) => ({ ...f, data: typeof updater === "function" ? updater(f.data) : updater }));

  const save = async () => {
    if (!form.organization_id) { toast.error("Lütfen bir tesis seçin"); setTab("info"); return; }
    if (!form.year) { toast.error("Rapor yılı zorunludur"); return; }
    setSaving(true);
    try {
      const payload = {
        organization_id: form.organization_id,
        year: Number(form.year),
        report_date: form.report_date,
        period_start: form.period_start,
        period_end: form.period_end,
        frequency: form.frequency,
        base_year: form.base_year ? Number(form.base_year) : Number(form.year),
        prepared_by_name: form.prepared_by_name,
        prepared_by_title: form.prepared_by_title,
        prepared_by_email: form.prepared_by_email,
        notes: form.notes,
        data: form.data,
      };
      let saved;
      if (id) saved = await updateReport(id, payload);
      else saved = await createReport(payload);
      toast.success("Rapor kaydedildi");
      if (!id) nav(`/reports/${saved.id}`, { replace: true });
      else setResults(saved.results || results);
    } catch (e) { toast.error("Kayıt başarısız"); }
    finally { setSaving(false); }
  };

  const onExport = async () => {
    if (!id) { toast.error("Önce kaydedin"); return; }
    try {
      toast.info("Word raporu hazırlanıyor…");
      const res = await exportReport(id);
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const org = orgs.find((o) => o.id === form.organization_id);
      a.href = url;
      a.download = `${(org?.name || "rapor").replace(/\s+/g, "_")}_Karbon_Raporu_${form.year}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Word raporu indirildi");
    } catch (e) { toast.error("İndirme başarısız"); }
  };

  const onExportPdf = async () => {};

  const total = results?.ozet?.total ?? 0;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading || !factors) {
    return <div className="flex items-center justify-center h-96 gap-2 text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Yükleniyor…
    </div>;
  }

  return (
    <div className="px-4 md:px-10 py-8 md:py-10 max-w-[1400px]">
      <Link to="/reports" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="size-4" /> Raporlar
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
            {id ? "Raporu Düzenle" : "Yeni Rapor"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold">
            {orgs.find((o) => o.id === form.organization_id)?.name || "Rapor"}
            <span className="text-muted-foreground font-normal text-2xl ml-3">· {form.year}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Anlık Toplam</div>
            <div className="font-display text-2xl font-semibold text-primary flex items-center gap-1.5">
              {calculating && <Loader2 className="size-3.5 animate-spin" />}
              {fmt(total, 2)} <span className="text-xs text-muted-foreground font-normal">tCO₂e</span>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full gap-2 h-10" data-testid="save-report-btn">
            <Save className="size-4" /> {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
          {id && (
            <Button variant="outline" onClick={onExport} className="rounded-full gap-2 h-10" data-testid="export-word-btn">
              <Download className="size-4" /> Word
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="h-auto flex-wrap bg-secondary/50 p-1 rounded-full">
          {TABS.map((t, i) => {
            const kat = t.id.startsWith("k") ? results?.[t.id]?.total : null;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`}
                           className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md gap-2 px-4 h-9">
                {t.label}
                {kat !== null && kat !== undefined && kat > 0 && (
                  <span className="text-[10px] opacity-70 font-normal">{fmt(kat, 2)}</span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-8">
          <TabsContent value="info">
            <ReportInfo form={form} set={set} orgs={orgs} />
          </TabsContent>
          <TabsContent value="k1"><Kategori1 factors={factors.k1} data={form.data.k1} setData={(fn) => setData((d) => ({ ...d, k1: fn(d.k1) }))} results={results?.k1} /></TabsContent>
          <TabsContent value="k2"><Kategori2 factors={factors.k2} data={form.data.k2} setData={(fn) => setData((d) => ({ ...d, k2: fn(d.k2) }))} results={results?.k2} /></TabsContent>
          <TabsContent value="k3"><Kategori3 factors={factors.k3} data={form.data.k3} setData={(fn) => setData((d) => ({ ...d, k3: fn(d.k3) }))} results={results?.k3} /></TabsContent>
          <TabsContent value="k4"><Kategori4 factors={factors.k4} data={form.data.k4} setData={(fn) => setData((d) => ({ ...d, k4: fn(d.k4) }))} results={results?.k4} /></TabsContent>
          <TabsContent value="k5"><Kategori5 factors={factors.k5} data={form.data.k5} setData={(fn) => setData((d) => ({ ...d, k5: fn(d.k5) }))} results={results?.k5} /></TabsContent>
          <TabsContent value="k6"><Kategori6 factors={factors.k6} data={form.data.k6} setData={(fn) => setData((d) => ({ ...d, k6: fn(d.k6) }))} results={results?.k6} /></TabsContent>
          <TabsContent value="summary"><Summary results={results} /></TabsContent>
        </div>
      </Tabs>

      {/* Navigation between tabs */}
      <div className="flex justify-between mt-10">
        <div />
        <Button variant="outline" className="rounded-full gap-2" onClick={() => {
          const i = TABS.findIndex((t) => t.id === tab);
          if (i < TABS.length - 1) setTab(TABS[i + 1].id);
        }} data-testid="next-tab-btn">
          Sonraki <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function mergeWithDefaults(d) {
  return {
    k1: { sabit_yanma: {}, hareketli_yanma: {}, kacak: {}, ...(d.k1 || {}) },
    k2: { kaynak_id: "el_tr", elektrik_yillik: 0, elektrik_aylik: {}, ...(d.k2 || {}) },
    k3: { calisan: {}, tasinim: {}, is_seyahati: {}, konaklama_ulke: "konak_turkiye", konaklama_fv: 0, ...(d.k3 || {}) },
    k4: { su: 0, atiksu: 0, atik: {}, satin_alinan_items: [], yakit_uretimi: {}, ...(d.k4 || {}) },
    k5: { urun_ton: 0, ef_override: {}, ...(d.k5 || {}) },
    k6: { kaynak_id: "k6_td_tr", yillik: 0, aylik: {}, ...(d.k6 || {}) },
  };
}
